import { useCallback, useEffect, useState } from "react";
import { Download, Loader2, Info, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import { formatINR, formatInt } from "../lib/format";
import { PageHeader } from "../components/common/PageHeader";
import { ProjectScopeBanner } from "../components/common/ProjectScopeBanner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

function monthDefault() {
  const t = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(new Date(t.getFullYear(), t.getMonth(), 1)), to: iso(new Date(t.getFullYear(), t.getMonth() + 1, 0)) };
}

export default function MemberRcReport() {
  const { has } = useAuth();
  const { ctx } = useWorkspace();
  const pid = ctx.project?.id;
  const canExport = has("REPORT.MEMBER_RC.EXPORT");
  const [range, setRange] = useState(monthDefault());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState("__all__");
  const [memberSearch, setMemberSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!range.from || !range.to) return;
    setLoading(true);
    api.get("/reports/member-rc/summary", {
      params: { ...(pid ? { project_id: pid } : {}), install_from: range.from, install_to: range.to },
    }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [pid, range.from, range.to]);
  useEffect(() => { load(); }, [load]);

  const requestExport = async () => {
    setBusy(true);
    try {
      const { data: job } = await api.post("/reports/exports", {
        report_type: "MEMBER_RC_WORK",
        filters: { ...(pid ? { project_id: pid } : {}), install_from: range.from, install_to: range.to },
        idempotency_key: crypto.randomUUID(),
      });
      toast.success(`Export requested (${job.status}). Track it in the Download Centre.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Export request failed"); }
    finally { setBusy(false); }
  };

  const canComm = data?.can_view_commercials;
  const showProjectCol = data?.project_scope === "all";
  const rows = (data?.rows || [])
    .filter((r) => roleFilter === "__all__" || r.role_label === roleFilter)
    .filter((r) => { const s = memberSearch.trim().toLowerCase(); return !s || (r.member_name || "").toLowerCase().includes(s) || (r.member_code || "").toLowerCase().includes(s); });

  return (
    <div>
      <PageHeader title="Member RC Work Report" subtitle="Member-wise associated RC work (Installation Date basis)" />
      <ProjectScopeBanner />

      <Tabs defaultValue="member" className="w-full">
        <TabsList>
          <TabsTrigger value="member" data-testid="mrc-tab-member">Member-wise</TabsTrigger>
          <TabsTrigger value="team" data-testid="mrc-tab-team">Team-wise</TabsTrigger>
        </TabsList>
        <TabsContent value="member" className="mt-4">
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge variant="outline" className="border-slate-300 text-xs font-medium text-slate-500">Date Basis: Installation Date</Badge>
          <span className="text-xs text-slate-500">From</span>
          <Input data-testid="mrc-from" type="date" value={range.from} max={range.to} onChange={(e) => setRange((s) => ({ ...s, from: e.target.value }))} className="h-9 w-40" />
          <span className="text-xs text-slate-500">To</span>
          <Input data-testid="mrc-to" type="date" value={range.to} min={range.from} onChange={(e) => setRange((s) => ({ ...s, to: e.target.value }))} className="h-9 w-40" />
          {(data?.roles || []).length > 0 && (
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger data-testid="mrc-role-filter" className="h-9 w-44"><SelectValue placeholder="All roles" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All roles</SelectItem>
                {data.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Input data-testid="mrc-member-search" value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="Search member name / code…" className="h-9 w-56" />
        </div>
        {canExport && (
          <Button data-testid="mrc-export" size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={requestExport} disabled={busy}>
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />} Request Export
          </Button>
        )}
      </div>

      <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="mrc-caveat">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>{data?.caveat || "Associated Work Amount attributes the full entry value to every member on the entry; totals across members can exceed the project total. Draft values are provisional."}</span>
      </div>

      {!canComm && data && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="mrc-no-commercial-note">
          <Lock className="h-3.5 w-3.5" /> Commercial amounts are restricted for your role — showing operational counts only.
        </div>
      )}

      {loading ? (
        <div className="grid place-items-center rounded-xl border border-slate-200 bg-white py-12"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : !data ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">No member RC work data for this range</div>
      ) : (
        <>
          <div className="mb-3 text-xs text-slate-500" data-testid="mrc-summary-line">{formatInt(data.total_rows)} member-role rows · {formatInt(data.distinct_members)} distinct members</div>

          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm md:block" data-testid="mrc-table-desktop">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100/80 text-xs uppercase tracking-wide text-slate-500">
                  {showProjectCol && <th className="px-3 py-2.5 font-semibold">Project</th>}
                  <th className="px-3 py-2.5 font-semibold">Member</th>
                  <th className="px-3 py-2.5 font-semibold">Role</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Completed</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Completed ₹</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Draft</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Draft Provisional ₹</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Unresolved</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Total</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Associated ₹</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={showProjectCol ? 10 : 9} className="px-3 py-8 text-center text-slate-400">No rows for this filter</td></tr>
                ) : rows.map((r) => (
                  <tr key={`${r.project_id}-${r.member_id}-${r.role}`} data-testid={`mrc-row-${r.member_id}-${r.role}`} className="border-b border-slate-100 hover:bg-slate-50">
                    {showProjectCol && <td className="px-3 py-2.5 text-slate-600">{r.project_name}</td>}
                    <td className="px-3 py-2.5"><div className="font-medium text-slate-800">{r.member_name}</div><div className="text-xs text-slate-400">{r.member_code || "—"} · {r.category_name}</div></td>
                    <td className="px-3 py-2.5"><Badge variant="secondary">{r.role_label}</Badge></td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{formatInt(r.completed_count)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">{formatINR(r.completed_amount)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{formatInt(r.draft_count)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-amber-700">{formatINR(r.draft_resolved_amount)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{r.draft_unresolved_count > 0 ? <span className="font-semibold text-red-600">{formatInt(r.draft_unresolved_count)}</span> : "0"}</td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{formatInt(r.total_count)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-indigo-700">{formatINR(r.associated_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden" data-testid="mrc-cards-mobile">
            {rows.map((r) => (
              <div key={`${r.project_id}-${r.member_id}-${r.role}`} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-slate-800">{r.member_name}</div>
                  <Badge variant="secondary">{r.role_label}</Badge>
                </div>
                <div className="mb-2 text-xs text-slate-400">{r.member_code || "—"} · {r.category_name}{showProjectCol ? ` · ${r.project_name}` : ""}</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-400">Completed</span><div className="font-semibold">{formatInt(r.completed_count)} · <span className="text-emerald-700">{formatINR(r.completed_amount)}</span></div></div>
                  <div><span className="text-slate-400">Draft (prov.)</span><div className="font-semibold">{formatInt(r.draft_count)} · <span className="text-amber-700">{formatINR(r.draft_resolved_amount)}</span></div></div>
                  <div><span className="text-slate-400">Unresolved</span><div className={r.draft_unresolved_count > 0 ? "font-semibold text-red-600" : "font-semibold"}>{formatInt(r.draft_unresolved_count)}</div></div>
                  <div><span className="text-slate-400">Associated</span><div className="font-semibold text-indigo-700">{formatINR(r.associated_amount)}</div></div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
        </TabsContent>
        <TabsContent value="team" className="mt-4">
          <TeamActivityTab pid={pid} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TeamActivityTab({ pid }) {
  const [range, setRange] = useState(monthDefault());
  const [single, setSingle] = useState(false);
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!range.from || !range.to) return;
    setLoading(true);
    api.get("/reports/member-rc/team-activity", {
      params: { ...(pid ? { project_id: pid } : {}), install_from: range.from, install_to: range.to, ...(search.trim() ? { search: search.trim() } : {}) },
    }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [pid, range.from, range.to, search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const canComm = data?.can_view_commercials;

  return (
    <div data-testid="mrc-team-tab">
      <div className="mb-3 flex flex-wrap items-end gap-2 text-sm">
        <Badge variant="outline" className="border-slate-300 text-xs font-medium text-slate-500">Date Basis: Installation Date</Badge>
        <Button data-testid="mrc-team-single-toggle" size="sm" variant={single ? "default" : "outline"} className={single ? "h-9 bg-teal-700 hover:bg-teal-800" : "h-9"} onClick={() => setSingle((s) => !s)}>Single Date</Button>
        {single ? (
          <div><span className="text-xs text-slate-500">Date</span><Input data-testid="mrc-team-day" type="date" value={range.from} onChange={(e) => setRange({ from: e.target.value, to: e.target.value })} className="ml-1 h-9 w-40" /></div>
        ) : (
          <>
            <span className="text-xs text-slate-500">From</span>
            <Input data-testid="mrc-team-from" type="date" value={range.from} max={range.to} onChange={(e) => setRange((s) => ({ ...s, from: e.target.value }))} className="h-9 w-40" />
            <span className="text-xs text-slate-500">To</span>
            <Input data-testid="mrc-team-to" type="date" value={range.to} min={range.from} onChange={(e) => setRange((s) => ({ ...s, to: e.target.value }))} className="h-9 w-40" />
          </>
        )}
        <Input data-testid="mrc-team-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search member name…" className="h-9 w-56" />
      </div>

      {data && !canComm && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="mrc-team-no-comm">
          <Lock className="h-3.5 w-3.5" /> Commercial amounts are restricted for your role — showing counts only.
        </div>
      )}

      {loading ? (
        <div className="grid place-items-center rounded-xl border border-slate-200 bg-white py-12"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : !data || (data.rows || []).length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">No team activity for this range</div>
      ) : (
        <>
          <div className="mb-2 text-xs text-slate-500" data-testid="mrc-team-count">{formatInt(data.total_teams)} team(s)</div>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="mrc-team-table">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100/80 text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2.5 font-semibold">Team (Members)</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Completed</th>
                  {canComm && <th className="px-3 py-2.5 text-right font-semibold">Completed ₹</th>}
                  <th className="px-3 py-2.5 text-right font-semibold">Draft</th>
                  {canComm && <th className="px-3 py-2.5 text-right font-semibold">Draft Prov. ₹</th>}
                  {canComm && <th className="px-3 py-2.5 text-right font-semibold">Unresolved</th>}
                  <th className="px-3 py-2.5 text-right font-semibold">Total Activity</th>
                  {canComm && <th className="px-3 py-2.5 text-right font-semibold">Associated ₹</th>}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.team_key} data-testid={`mrc-team-row-${r.team_key}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-1.5">
                        {r.members.map((m) => (
                          <span key={m.member_id} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs">
                            <span className="font-medium text-slate-800">{m.member_name}</span>
                            <span className="text-slate-400">{m.role_label}{m.member_code ? ` · ${m.member_code}` : ""}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-emerald-700">{formatInt(r.completed_count)}</td>
                    {canComm && <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700">{formatINR(r.completed_amount)}</td>}
                    <td className="px-3 py-2.5 text-right tabular-nums text-amber-700">{formatInt(r.draft_count)}</td>
                    {canComm && <td className="px-3 py-2.5 text-right tabular-nums text-amber-700">{formatINR(r.draft_resolved_amount)}</td>}
                    {canComm && <td className="px-3 py-2.5 text-right tabular-nums">{r.draft_unresolved_count > 0 ? <span className="font-semibold text-red-600">{formatInt(r.draft_unresolved_count)}</span> : "0"}</td>}
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{formatInt(r.total_count)}</td>
                    {canComm && <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-indigo-700">{formatINR(r.associated_amount)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
